import { useCallback, useEffect, useRef, useState } from 'react'
import {
  searchOrganizations,
  searchCompanies,
  searchProperties,
} from '../services/reimbursementsService'
import type {
  OrganizationOption,
  CompanyOption,
  PropertyOption,
  SearchableOption,
} from '../types'

const DEFAULT_DEBOUNCE_MS = 400

function useAutocomplete<T>(
  fetcher: (q: string) => Promise<T[]>,
  toOption: (item: T) => SearchableOption,
  initialOptions: SearchableOption[] = [],
  shouldFetch?: (q: string, isPaste?: boolean) => boolean,
): {
  options: SearchableOption[]
  loading: boolean
  search: (q: string, isPaste?: boolean) => void
} {
  const [options, setOptions] = useState<SearchableOption[]>(initialOptions)
  const [loading, setLoading] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  const toOptionRef = useRef(toOption)
  toOptionRef.current = toOption
  const initialOptionsRef = useRef(initialOptions)
  initialOptionsRef.current = initialOptions
  const shouldFetchRef = useRef(shouldFetch)
  shouldFetchRef.current = shouldFetch

  // Sync initialOptions when passed in props change
  useEffect(() => {
    if (initialOptions.length > 0) {
      setOptions((prev) => {
        const newMap = new Map<string, SearchableOption>()
        initialOptions.forEach((opt) => newMap.set(String(opt.value), opt))
        prev.forEach((opt) => newMap.set(String(opt.value), opt))
        return Array.from(newMap.values())
      })
    }
  }, [initialOptions])

  const search = useCallback((q: string, isPaste?: boolean) => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (shouldFetchRef.current && !shouldFetchRef.current(q, isPaste)) {
      return
    }
    setLoading(true)
    timerRef.current = setTimeout(async () => {
      try {
        const results = await fetcherRef.current(q)
        const fetchedOptions = results.map((r) => toOptionRef.current(r))
        setOptions((prev) => {
          const newMap = new Map<string, SearchableOption>()
          initialOptionsRef.current.forEach((opt) => newMap.set(String(opt.value), opt))
          prev.forEach((opt) => newMap.set(String(opt.value), opt))
          fetchedOptions.forEach((opt) => newMap.set(String(opt.value), opt))
          return Array.from(newMap.values())
        })
      } catch {
        // Keep existing options on error
      } finally {
        setLoading(false)
      }
    }, DEFAULT_DEBOUNCE_MS)
  }, [])

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  return { options, loading, search }
}

export function useOrganizationAutocomplete(initialOptions?: SearchableOption[]) {
  return useAutocomplete<OrganizationOption>(
    (q) => searchOrganizations(q),
    (o) => ({ value: String(o.org_id), label: o.org_name }),
    initialOptions,
    (q, isPaste) => isPaste || q.length === 0 || q.length % 2 === 0,
  )
}

export function useCompanyAutocomplete(orgId?: number | string, initialOptions?: SearchableOption[]) {
  return useAutocomplete<CompanyOption>(
    (q) => searchCompanies(q, orgId),
    (c) => ({ value: String(c.company_id), label: c.company_name }),
    initialOptions,
    (q, isPaste) => isPaste || q.length === 0 || q.length >= 2,
  )
}

export function usePropertyAutocomplete(
  orgId?: number | string,
  companyId?: number | string,
  initialOptions?: SearchableOption[],
) {
  return useAutocomplete<PropertyOption>(
    (q) => searchProperties(q, orgId, companyId),
    (p) => ({ value: String(p.property_id), label: p.property_name }),
    initialOptions,
    (q, isPaste) => isPaste || q.length === 0 || q.length % 2 === 0,
  )
}
